import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { SubscriptionPlanForm } from "@/components/admin/subscriptions/SubscriptionPlanForm";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { toast } from "sonner";
import { SubscriptionPlan } from "@/lib/types";

export default function SubscriptionPlanEditPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const navigate = useNavigate();
  const { planId } = useParams<{ planId: string }>();
  
  const { useUpdateSubscriptionPlanMutation, useSubscriptionPlanByIdQuery } = useSubscriptions();
  
  const updatePlan = useUpdateSubscriptionPlanMutation();
  const { data: planData, isLoading } = useSubscriptionPlanByIdQuery(planId);
  
  useEffect(() => {
    if (planData) {
      setPlan(planData);
    }
  }, [planData]);
  
  const handleSubmit = async (data: any) => {
    if (!plan) return;
    
    setIsSubmitting(true);
    try {
      const features = {
        currency_conversion: data.features?.currency_conversion || false,
        multiple_currencies: data.features?.multiple_currencies || false,
        price_comparison: data.features?.price_comparison || false,
        grouped_contracts: data.features?.grouped_contracts || false,
        contract_services: data.features?.contract_services || false,
        reminders: data.features?.reminders || false,
        payment_type_selection: data.features?.payment_type_selection || false,
        dashboard: data.features?.dashboard || false,
        contract_pdf_email: data.features?.contract_pdf_email || false
      };

      await updatePlan.mutateAsync({
        ...plan,
        ...data,
        features,
        updated_at: new Date().toISOString()
      });
      
      toast.success("Subscription plan updated successfully");
      navigate("/admin", { state: { from: "subscription-plans" } });
    } catch (error) {
      console.error("Failed to update plan:", error);
      toast.error("Failed to update subscription plan. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    navigate("/admin", { state: { from: "subscription-plans" } });
  };
  
  if (isLoading) {
    return (
      <div className="container py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin", { state: { from: "subscription-plans" } })}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Edit Subscription Plan</h1>
        </div>
        <div className="flex items-center justify-center h-64">
          <p>Loading plan details...</p>
        </div>
      </div>
    );
  }
  
  if (!plan && !isLoading) {
    return (
      <div className="container py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin", { state: { from: "subscription-plans" } })}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Edit Subscription Plan</h1>
        </div>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-red-500">Subscription plan not found</p>
          <Button 
            onClick={() => navigate("/admin", { state: { from: "subscription-plans" } })} 
            className="mt-4"
          >
            Return to Plans
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/admin", { state: { from: "subscription-plans" } })}
          className="h-8 w-8"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Edit Subscription Plan</h1>
      </div>
      
      <div className="bg-card rounded-lg border shadow">
        <div className="p-6">
          <SubscriptionPlanForm 
            onSubmit={handleSubmit} 
            onCancel={handleCancel}
            plan={plan}
          />
        </div>
      </div>
    </div>
  );
}
